import { useQuery } from '@tanstack/react-query';
import {
	Grid3x2Icon,
	ListIcon,
	LogOutIcon,
	MapIcon,
	Plus,
	Search,
	SettingsIcon,
	UserIcon,
	SunIcon,
	MoonIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ROLE } from '../common/role';
import { useAppContext } from '../context/AppContext';
import { useKeycloak } from '../context/KeycloakContext';
import { getProfileOptions } from '../queries/users/profile';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Restricted } from './Restricted';
import { SelectOrganizationPopover } from './Organizations/SelectOrganizationPopup';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useTheme } from '@/context/ThemeContext';

export interface MainHeaderProps {
	searchTerm: string;
	onSearchTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	organizationFilter: string;
	onOrganizationFilterChange: (val: string) => void;
	organizations: any[];
	isSuperAdmin: boolean;
	viewMode: AssetListViewMode;
	onViewModeChange: (val: AssetListViewMode) => void;
	/** The view mode cannot change, e.g. while deleted assets are shown (compact list only) */
	viewModeLocked?: boolean;
	/** State of the organization filter's "Show inactive" switch */
	showInactiveOrganizations?: boolean;
	onShowInactiveOrganizationsChange?: (showInactive: boolean) => void;
}

/** Cards, compact table, or map. */
export type AssetListViewMode = 'grid' | 'list' | 'map';

export function MainHeader({
	searchTerm,
	onSearchTermChange,
	organizationFilter,
	onOrganizationFilterChange,
	organizations,
	isSuperAdmin,
	viewMode,
	onViewModeChange,
	viewModeLocked = false,
	showInactiveOrganizations,
	onShowInactiveOrganizationsChange,
}: MainHeaderProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: user } = useQuery(getProfileOptions());
	const { onLogout } = useAppContext();
	const { keycloak } = useKeycloak();

	const { toggleTheme, isDarkTheme } = useTheme();

	const organization = useMemo(() => {
		const org = keycloak?.tokenParsed?.organization;
		if (typeof org === 'string') {
			return org;
		}
		return Object.keys(org || {})[0] || '';
	}, [keycloak?.tokenParsed?.organization]);

	const isOrgSelected = !isSuperAdmin || (organizationFilter && organizationFilter !== 'all');
	// Inactive organizations are read-only, so no asset can be created in them
	const isSelectedOrgInactive =
		isSuperAdmin && organizations.find((org) => org.id === organizationFilter)?.enabled === false;
	const canCreateAsset = isOrgSelected && !isSelectedOrgInactive;
	const createAssetHint = isSelectedOrgInactive
		? t('assets.list.orgInactiveNoNewAssets')
		: isOrgSelected
			? t('assets.list.createNewAsset')
			: t('assets.list.selectOrgToAddAsset');

	const viewToggle = (
		<ToggleGroup
			type="single"
			value={viewMode}
			disabled={viewModeLocked}
			onValueChange={(value) => value && onViewModeChange(value as AssetListViewMode)}
			className="bg-muted p-1 border border-border rounded-lg shrink-0 shadow-inner"
		>
			<ToggleGroupItem
				className="h-8 px-3 rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center gap-2"
				value="grid"
				aria-label={t('assets.list.viewGrid')}
			>
				<Grid3x2Icon className="h-4 w-4" />
			</ToggleGroupItem>
			<ToggleGroupItem
				className="h-8 px-3 rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center gap-2"
				value="list"
				aria-label={t('assets.list.viewList')}
				title={t('assets.list.viewList')}
			>
				<ListIcon className="h-4 w-4" />
			</ToggleGroupItem>
			<ToggleGroupItem
				className="h-8 px-3 rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center gap-2"
				value="map"
				aria-label={t('assets.list.viewMap')}
			>
				<MapIcon className="h-4 w-4" />
			</ToggleGroupItem>
		</ToggleGroup>
	);

	const themeAndUser = (
		<>
			<LanguageSwitcher />
			<Button
				variant="ghost"
				size="icon"
				onClick={toggleTheme}
				className="relative h-9 w-9 rounded-full bg-muted/30 border border-border p-0 shadow-sm hover:bg-muted/50 transition-colors"
				title={t('common.theme.toggle')}
			>
				<SunIcon className="h-4.5 w-4.5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 text-muted-foreground" />
				<MoonIcon className="absolute h-4.5 w-4.5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100 text-muted-foreground" />
				<span className="sr-only">{t('common.theme.toggle')}</span>
			</Button>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="relative h-9 w-9 rounded-full bg-muted/30 border border-border p-0 shadow-sm hover:bg-muted/50 transition-colors"
					>
						<UserIcon className="size-4.5 text-muted-foreground" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					className="w-56 bg-popover border-border text-popover-foreground shadow-lg rounded-xl mt-1"
					align="end"
					forceMount
				>
					<DropdownMenuLabel className="font-normal py-3 px-3">
						<div className="flex flex-col space-y-1">
							<p className="text-sm font-semibold leading-none text-foreground">{user?.data?.name}</p>
							<p className="text-xs leading-none text-muted-foreground">{organization}</p>
						</div>
					</DropdownMenuLabel>
					<DropdownMenuSeparator className="bg-border" />
					<Restricted some to={[ROLE.ORGANIZATION_READ, ROLE.USERS_READ]}>
						<DropdownMenuItem
							className="cursor-pointer focus:bg-muted focus:text-foreground py-2.5 rounded-md mx-1"
							onClick={() => navigate('/administration')}
						>
							<SettingsIcon className="mr-2 h-4 w-4 text-muted-foreground" />
							<span className="font-medium text-sm">{t('common.actions.administration')}</span>
						</DropdownMenuItem>
					</Restricted>
					<DropdownMenuItem
						className="text-destructive cursor-pointer focus:bg-destructive/10 focus:text-destructive py-2.5 rounded-md mx-1 mt-1 mb-1"
						onClick={onLogout}
					>
						<LogOutIcon className="mr-2 h-4 w-4" />
						<span className="font-medium text-sm">{t('common.actions.logout')}</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</>
	);

	return (
		<header className="flex-none border-b border-border bg-card/95 backdrop-blur-md z-30 transition-all">
			{/* ── MOBILE / TABLET layout (< lg): single row ── */}
			<div className="lg:hidden px-4 h-14 flex items-center gap-3">
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: need for click */}
				<img
					src="/logo.png"
					alt={t('assets.shell.logoAlt')}
					className="h-8 w-auto shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
					onClick={() => navigate('/')}
				/>
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						type="text"
						className="h-9 w-full rounded-full border-border bg-input/80 pl-9 pr-4 text-sm focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring transition-all shadow-inner"
						placeholder={t('assets.list.searchPlaceholder')}
						value={searchTerm}
						onChange={onSearchTermChange}
						autoComplete="off"
					/>
				</div>
				<Button
					variant="ghost"
					size="icon"
					onClick={() => onViewModeChange(viewMode === 'map' ? 'grid' : 'map')}
					disabled={viewModeLocked}
					className="relative h-9 w-9 rounded-full bg-muted/30 border border-border p-0 shadow-sm hover:bg-muted/50 transition-colors shrink-0"
					title={viewMode === 'map' ? t('assets.list.showList') : t('assets.list.showMap')}
				>
					{viewMode === 'map' ? (
						<Grid3x2Icon className="h-4 w-4 text-muted-foreground" />
					) : (
						<MapIcon className="h-4 w-4 text-muted-foreground" />
					)}
				</Button>
				<div className="flex items-center gap-2 shrink-0">{themeAndUser}</div>
			</div>

			{/* ── DESKTOP layout (lg+): single row ── */}
			<div className="hidden lg:flex h-16 px-4 items-center justify-between">
				{/* Logo */}
				<div className="flex items-center gap-4 shrink-0">
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: need for click */}
					<img
						src="/logo_with_text_cropped.png"
						alt={t('assets.shell.logoAlt')}
						className="w-28 cursor-pointer hover:opacity-80 transition-opacity"
						onClick={() => navigate('/')}
					/>
				</div>

				{/* Middle: Search, Filters */}
				<div className="flex flex-1 items-center justify-center px-2 gap-2">
					<div className="relative w-full max-w-65 focus-within:max-w-[320px] transition-all duration-300 ease-in-out">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							type="text"
							className="h-9 w-full rounded-full border-border bg-input/80 pl-9 pr-4 text-sm focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring transition-all shadow-inner"
							placeholder={t('assets.list.searchPlaceholder')}
							value={searchTerm}
							onChange={onSearchTermChange}
							autoComplete="off"
						/>
					</div>

					<Restricted to={ROLE.ASSETS_CREATE}>
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<div tabIndex={0} className="inline-block cursor-pointer">
										<Button
											size="icon"
											disabled={!canCreateAsset}
											className={`h-8.5 w-8.5 shrink-0 rounded-4xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm flex active:scale-95 transition-all ${!canCreateAsset ? 'opacity-50 pointer-events-none' : ''}`}
											onClick={(e) => {
												if (!canCreateAsset) {
													e.preventDefault();
													return;
												}
												const queryStr = isSuperAdmin ? `?orgid=${organizationFilter}` : '';
												navigate(`/assets/new${queryStr}`);
											}}
										>
											<Plus className="w-5 h-5" />
										</Button>
									</div>
								</TooltipTrigger>
								<TooltipContent side="bottom" className="text-xs">
									<p>{createAssetHint}</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</Restricted>

					<div className="h-5 w-px bg-border mx-1" />

					{isSuperAdmin && (
						<div className="relative shrink-0 min-w-44 max-w-64">
							<SelectOrganizationPopover
								asFilter
								addAllItem
								inactiveToggle
								showInactive={showInactiveOrganizations}
								onShowInactiveChange={onShowInactiveOrganizationsChange}
								organizations={organizations}
								onChange={onOrganizationFilterChange}
								value={organizationFilter}
							/>
						</div>
					)}
				</div>

				{/* Right: View Toggle + Theme + User */}
				<div className="flex items-center gap-3 shrink-0">
					{viewToggle}
					<div className="h-5 w-px bg-border mx-1" />
					{themeAndUser}
				</div>
			</div>
		</header>
	);
}
